import type { TrackState } from '@melodinai/types';
import { useMixerStore } from '../../stores/mixerStore';
import { useStudioStore } from '../../stores/studioStore';
import { suggestForTrack } from './ai/mixHeuristics';
import { clsx } from '../../lib/utils';

const ACTIONS = [
  { id: 'addPunch' as const, label: 'Add Punch' },
  { id: 'addWeight' as const, label: 'Add Weight' },
  { id: 'addAir' as const, label: 'Add Air' },
  { id: 'addWidth' as const, label: 'Add Width' },
  { id: 'fixMud' as const, label: 'Fix Mud' },
  { id: 'improveClarity' as const, label: 'Improve Clarity' },
];

interface AiMixAssistantProps {
  track: TrackState;
}

export function AiMixAssistant({ track }: AiMixAssistantProps) {
  const applyAiMixAction = useMixerStore((s) => s.applyAiMixAction);
  const meters = useMixerStore((s) => s.meters[track.id]);
  const allTracks = useStudioStore((s) => s.tracks);
  const suggestions = suggestForTrack(track, meters, allTracks);

  return (
    <section className="space-y-2 rounded border border-ssl-metal/50 bg-black/25 p-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">AI Mix</h3>
        <button
          type="button"
          className="rounded border border-ssl-accent/40 px-1.5 py-0.5 text-[9px] text-ssl-accent"
          onClick={() => applyAiMixAction(track.id, 'analyze')}
        >
          Analyze
        </button>
      </div>
      <ul className="space-y-1">
        {suggestions.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              className="w-full rounded px-1.5 py-1 text-left text-[10px] text-ssl-text hover:bg-ssl-rail"
              onClick={() => s.action && applyAiMixAction(track.id, s.action)}
            >
              {s.text}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-1 pt-1">
        {ACTIONS.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => applyAiMixAction(track.id, a.id)}
            className={clsx(
              'rounded border border-ssl-metal/50 px-1.5 py-0.5 text-[9px] text-ssl-muted',
              'hover:border-ssl-accent hover:text-ssl-accent',
            )}
          >
            {a.label}
          </button>
        ))}
      </div>
    </section>
  );
}
